import SelectViewingGroups from "@components/auto-completes/viewing-groups";
import {
  SHBooleanField,
  SHHtmlBlock,
  SHLabel,
  SHStack,
  SHTypography,
} from "@components/design-systems";
import { MAX_LONG_TEXT_LENGTH } from "@constants";
import {
  useEditModelPaths,
  useInputDotIndicator,
  useUpdateSectionData,
} from "@hooks/useInvestmentProduct";
import { FieldTypeId, HighlightType } from "@models/configuration";
import { ProductDataStatus } from "@models/product/enums/productDataStatus";
import { InvestmentProductModelsDTO } from "@models/product/investment-product/entities/model";
import { InvestmentBannerStatus } from "@models/product/investment-product/enums/status";
import { toStringValue } from "@pages/suppliers/_id/_products/_id/edit/components/tab-form/util";
import { CustomLabel } from "@pages/suppliers/_id/_products/investment-product/_id/edit/components/custom-label";
import { DEBOUNCE_DELAY } from "@pages/suppliers/_id/_products/investment-product/_id/edit/components/tabs/model/configs";
import { castToViewingGroupSelectionDTO } from "@pages/suppliers/_id/_products/investment-product/_id/edit/components/tabs/model/utils";
import { RootState } from "@redux/store";
import { debounce, defaultTo, isEmpty, isEqual } from "lodash";
import { useMemo } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import { EditProductDefaults } from "../../../../../../constants";
import { InvestmentProductConfigurationFeature } from "@models/product/investment-product/entities/investmentProduct";
import SHRichTextEditorV2 from "@components/design-systems/sh-rich-text-editor-v2";

interface ModelFeatureYesNoFieldProps {
  selectedIndex: [number, number];
  sectionIndex: number;
  featureIndex: number;
  feature: InvestmentProductConfigurationFeature;
}

export const ModelFeatureYesNoField = ({
  selectedIndex,
  sectionIndex,
  featureIndex,
  feature,
}: ModelFeatureYesNoFieldProps) => {
  const {
    investmentProductUI: { isSaving },
    investmentProductBannerInfo,
  } = useSelector((state: RootState) => state.investmentProduct);
  const isPendingApproval = investmentProductBannerInfo?.some((banner) =>
    isEqual(banner.status, InvestmentBannerStatus.PendingApproval),
  );
  const originalValue = defaultTo(feature.productData?.value, "");
  const originalAdditionalValue = defaultTo(
    feature.productData?.additionalValue,
    "",
  );

  /** Hook */
  const { control, setValue, getValues } = useFormContext<{
    investmentProductModels: InvestmentProductModelsDTO[];
  }>();
  const { featureProductDataPath, featurePath } = useEditModelPaths(
    selectedIndex,
    sectionIndex,
    featureIndex,
  );
  const watchedValue = useWatch({
    control,
    name: [featureProductDataPath.value],
  });
  const productDataValue = useMemo(() => {
    const value = getValues(featureProductDataPath.value);
    if (isEmpty(value)) return null;
    return isEqual(value, "true");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedValue, featureIndex, selectedIndex, sectionIndex]);

  const { isShowOrangeDot, isShowBlueDot } = useInputDotIndicator<{
    investmentProductModels: InvestmentProductModelsDTO[];
  }>({
    getValues,
    control,
    paths: {
      highlightType: featureProductDataPath.highlightType,
      dataStatus: featureProductDataPath.dataStatus,
    },
  });
  const handleUpdateSectionData = useUpdateSectionData(
    selectedIndex,
    sectionIndex,
  );

  const displayAdditionalValueField = isEqual(productDataValue, true);
  /** State */

  /** Handler */
  const handleInputChange = (newValue: string | null) => {
    setValue(
      featureProductDataPath.dataStatus,
      isEmpty(newValue)
        ? ProductDataStatus.MissingData
        : ProductDataStatus.Filled,
    );
    setValue(
      featurePath.dataStatus,
      isEmpty(newValue)
        ? ProductDataStatus.MissingData
        : ProductDataStatus.Filled,
    );
    setValue(featurePath.isModified, !isEqual(newValue, originalValue));
    setValue(
      featureProductDataPath.isModified,
      !isEqual(newValue, originalValue),
    );
    const publishedValue = defaultTo(
      getValues(featureProductDataPath.publishedValue),
      "",
    );

    if (!isEqual(publishedValue, newValue)) {
      setValue(featureProductDataPath.highlightType, HighlightType.Edited);
      setValue(featurePath.highlightType, HighlightType.Edited);
    } else {
      setValue(featureProductDataPath.highlightType, null);
      setValue(featurePath.highlightType, null);
    }
    handleUpdateSectionData();
  };

  const handleEditManagerAdditionalFieldChange = (newValue: string) => {
    setValue(
      featurePath.isModified,
      !isEqual(newValue, originalAdditionalValue),
    );
    setValue(
      featureProductDataPath.isModified,
      !isEqual(newValue, originalAdditionalValue),
    );

    const publishedAdditionalValue = defaultTo(
      getValues(featureProductDataPath.publishedAdditionalValue),
      "",
    );

    if (!isEqual(publishedAdditionalValue, newValue)) {
      setValue(featureProductDataPath.highlightType, HighlightType.Edited);
      setValue(featurePath.highlightType, HighlightType.Edited);
    } else {
      const value = defaultTo(getValues(featureProductDataPath.value), "");
      const publishedValue = defaultTo(
        getValues(featureProductDataPath.publishedValue),
        "",
      );
      const highlightType = isEqual(value, publishedValue)
        ? null
        : HighlightType.Edited;

      setValue(featureProductDataPath.highlightType, highlightType);
      setValue(featurePath.highlightType, highlightType);
    }
    handleUpdateSectionData();
  };

  const debouncedHandleInputChange = debounce(
    handleInputChange,
    DEBOUNCE_DELAY,
  );

  /** Side effect */

  return (
    <>
      <SHStack direction="column" gap={2} width={"100%"}>
        <SHStack>
          <SHLabel
            label={
              <CustomLabel
                featureData={feature}
                disableInformationButton
                dotProps={{
                  orangeDot: isShowOrangeDot,
                  blueDot: isShowBlueDot,
                  greenDot: feature.isBusinessMetric,
                }}
              />
            }
          />
          <SHHtmlBlock variant="body3" content={feature.description} />
        </SHStack>
        {feature.allowedVisibilitySetting && (
          <SHStack>
            <SHLabel
              label={
                <SHTypography variant="subtitle2">Visible to</SHTypography>
              }
            />
            <Controller
              name={featurePath.viewingGroupSelections}
              control={control}
              render={({ field: { ref, ...other } }) => (
                <SelectViewingGroups
                  disabled={isSaving || isPendingApproval}
                  viewingGroupData={feature.viewingGroupSelections ?? []}
                  value={
                    castToViewingGroupSelectionDTO(
                      getValues(featurePath.viewingGroupSelections),
                    ) ?? []
                  }
                  onChange={(value) => {
                    other.onChange(value);
                    setValue(
                      featurePath.isModified,
                      !isEqual(feature.viewingGroupSelections, value),
                    );
                    handleUpdateSectionData();
                  }}
                />
              )}
            />
          </SHStack>
        )}
      </SHStack>

      <SHStack>
        <Controller
          control={control}
          name={featureProductDataPath["value"]}
          render={({ field: { ref, ...other } }) => (
            <SHBooleanField
              sx={{ width: "520px" }}
              disabled={isSaving || isPendingApproval}
              value={productDataValue}
              onChange={(_, value?: boolean | null) => {
                const newValue = toStringValue(value, FieldTypeId.YesNo);
                other.onChange(newValue);
                debouncedHandleInputChange(newValue);
              }}
            />
          )}
        />
        {displayAdditionalValueField && (
          <SHStack sx={{ width: "520px", height: "280px" }}>
            <Controller
              name={featureProductDataPath.additionalValue as any}
              control={control}
              render={({ field: { ref, ...other } }) => (
                <SHRichTextEditorV2
                  debounceAbortMethod={"flush"}
                  isDisabled={isSaving || isPendingApproval}
                  value={other.value}
                  label={EditProductDefaults.AdditionalFieldTitle}
                  maxCharacters={MAX_LONG_TEXT_LENGTH}
                  onChange={(newValue) => {
                    other.onChange(newValue);
                    handleEditManagerAdditionalFieldChange(newValue);
                  }}
                />
              )}
            />
          </SHStack>
        )}
      </SHStack>
    </>
  );
};
